import classNames from "classnames"
import { ReactElement, ReactNode } from "react"
import { SwimmingPoolConfiguratorShapeKind, SwimmingPoolConfiguratorShapeOrientation, SwimmingPoolConfiguratorVariantFragment, SwimmingPoolConfiguratorVariantProductFragment, UUID } from "../model/fragments"

const DIMENSION_LARGE_SIZE_BOUNDARY = 14000;

export class SwimmingPoolProductMissingError extends Error
{}

export enum SwimmingPoolSchemaDimensionsType {
	number = 'number',
	letter = 'letter',
	none = 'none'
}

export type SwimmingPoolConfiguratorDTO = {
	shape: UUID,
	variant: UUID,
	spec: string
}

export type SwimmingPoolSpecData = {
	kind: SwimmingPoolConfiguratorShapeKind,
	length: number,
	direction?: string
	ref: number | null,
	editable: boolean,
	subtractStart: number,
	subtractEnd: number,
	product?: SwimmingPoolConfiguratorVariantProductFragment
}

export type SwimmingPoolCoordData = SwimmingPoolSpecData & 
{
	start: number[],
	end: number[]
	startAngle: number 
	endAngle: number
	ref: number | null,
	previousStartAngle?: number
	nextEndAngle?: number
};

export type SwimmingPoolConstructionDetails = 
{
	innerWidth: number,
	innerLength: number,

	outerWidth: number,
	outerLength: number,

	circumference: number

	reserveCounts: {[prop: string] : number}
	productCounts: {[prop: string] : number}
	productExactCounts: {[prop: string] : number}
}

type SwimmingPoolConfiguratorElementsFromCoordsOut = 
{
	elems: ReactNode[]
	middleJoints: number
}

type ViewBox = number[]

export const parseSwimmingPoolSpec = (spec: string) : SwimmingPoolSpecData[] => {

	const memory : SwimmingPoolSpecData[] = [];
	const add = (i: string) => 
	{
		switch(i[0])
		{
			case SwimmingPoolConfiguratorShapeKind.middle:
			case SwimmingPoolConfiguratorShapeKind.middleSpacer:
			{
				memory.push({
					kind: i[0] as SwimmingPoolConfiguratorShapeKind,
					length: parseFloat(i.replace(/[a-zA-Z]/g, '')),
					ref: null,
					editable: true,
					subtractStart: 0,
					subtractEnd: 0
				});
				break;
			}
			case SwimmingPoolConfiguratorShapeKind.radius:
			{
				memory.push({
					kind: i[0] as SwimmingPoolConfiguratorShapeKind,
					length: parseFloat(i.replace(/[a-zA-Z]/g, '')),
					direction: i[i.length - 1],
					editable: false,
					ref: null,
					subtractStart: 0,
					subtractEnd: 0
				});
				break;
			}
			case SwimmingPoolConfiguratorShapeKind.corner:
			{
				memory.push({
					kind: i[0] as SwimmingPoolConfiguratorShapeKind,
					length: 0,
					direction: i[i.length - 1],
					editable: false,
					ref: null,
					subtractStart: 0,
					subtractEnd: 0
				});
				break;
			}

			case 'D': 
			{
				const index = parseInt(i.replace(/[a-zA-Z]/g, '')) - 1;
				const it = memory[index];
				const clone = JSON.parse(JSON.stringify(it)) as SwimmingPoolSpecData;
				clone.editable = false;
				if(clone.kind == SwimmingPoolConfiguratorShapeKind.middleSpacer)
				{
					clone.kind = SwimmingPoolConfiguratorShapeKind.middle;
				}

				clone.ref = index;
				memory.push(clone);
				break;
			}
			default: throw new Error('Unrecognizable spec: ' + i);
		}
	};

	for(const s of spec.split(' ').filter(s => s))
	{
		add(s);
	}

	return memory;
};

export const stringifySwimmingPoolSpec = (spec: SwimmingPoolSpecData[]) : string =>
{
	return spec.map(s => {

		if(s.ref !== null)
		{
			return `D${s.ref+1}`;
		}

		switch(s.kind)
		{
			case SwimmingPoolConfiguratorShapeKind.middle:
			case SwimmingPoolConfiguratorShapeKind.middleSpacer:
				return `${s.kind}${Math.round(s.length)}`;
			case SwimmingPoolConfiguratorShapeKind.corner:
				return `${s.kind}${s.direction}`;
			case SwimmingPoolConfiguratorShapeKind.radius:
				return `${s.kind}${Math.round(s.length)}${s.direction}`;
			default: throw new Error('Unknown spec kind: ' + s.kind);
		}

	}).join(' ');
}

export const checkVariantProductAvailability = (data: SwimmingPoolSpecData[], variant: SwimmingPoolConfiguratorVariantFragment) => 
{
	try 
	{
		determineProductsAndLengthsForSpecData(data, variant);
		return true;
	}
	catch(err)
	{
		if(!(err instanceof SwimmingPoolProductMissingError))
		{
			throw err;
		}

		return false;
	}
}

const determineProductsAndLengthsForSpecData = (data: SwimmingPoolSpecData[], variant: SwimmingPoolConfiguratorVariantFragment) => 
{
	for(const info of data)
	{
		if(info.kind == SwimmingPoolConfiguratorShapeKind.middle)
		{
			const product = variant.products.find(p => p.kind == SwimmingPoolConfiguratorShapeKind.middle);
			if(!product)
			{
				throw new SwimmingPoolProductMissingError();
			}

			info.product = product;
		}
		else if(info.kind == SwimmingPoolConfiguratorShapeKind.radius)
		{
			const product = variant.products.find(p => p.kind == SwimmingPoolConfiguratorShapeKind.radius && p.length == info.length);
			if(!product)
			{
				throw new SwimmingPoolProductMissingError();
			}

			info.product = product;
			info.length = info.product.length;
		}
		else if(info.kind == SwimmingPoolConfiguratorShapeKind.corner)
		{
			const orientation = info.direction == 'l' ? SwimmingPoolConfiguratorShapeOrientation.outer : SwimmingPoolConfiguratorShapeOrientation.inner;
			const product = variant.products.find(p => p.kind == SwimmingPoolConfiguratorShapeKind.corner && p.orientation == orientation);
			if(!product)
			{
				throw new SwimmingPoolProductMissingError();
			}

			info.product = product;
			info.length = info.product.length + (info.direction == 'l' ? (0) : (-info.product!.thickness + info.product!.overlap));
		}
	}

	return data;
}


const subtractAnglesFromMiddleSpecPoints = (memory: SwimmingPoolSpecData[]) => 
{
	// Dimensions will specify whole side always
	for(let i = 0;i < memory.length;i++)
	{
		const previous = i == 0 ? memory[memory.length - 1] : memory[i - 1];
		const current = memory[i];
		const next = i == (memory.length - 1) ? memory[0] : memory[i + 1];

		if(current.kind == SwimmingPoolConfiguratorShapeKind.middle || current.kind == SwimmingPoolConfiguratorShapeKind.middleSpacer)
		{
			if(previous.kind != SwimmingPoolConfiguratorShapeKind.middle && previous.kind != SwimmingPoolConfiguratorShapeKind.middleSpacer)
			{
				current.length -= previous.length;
				current.subtractStart = previous.length;
			}

			if(next.kind != SwimmingPoolConfiguratorShapeKind.middle && next.kind != SwimmingPoolConfiguratorShapeKind.middleSpacer)
			{
				current.length -= next.length;
				current.subtractEnd = next.length;
			}

		}
	}

	return memory;
}

const determineNeighbourAngles = (memory: SwimmingPoolCoordData[]) => 
{
	for(let i = 0;i < memory.length;i++)
	{
		const previous = i == 0 ? memory[memory.length - 1] : memory[i - 1];
		const current = memory[i];
		const next = i == (memory.length - 1) ? memory[0] : memory[i + 1];

		current.previousStartAngle = previous.startAngle;
		current.nextEndAngle = next.endAngle;
	}

	return memory;
}


const makeCoordDataFromSpecData = (specs: SwimmingPoolSpecData[], withDimensions: SwimmingPoolSchemaDimensionsType) : [ViewBox, SwimmingPoolCoordData[]] => {

	let boundary : (number | null)[] = [null, null, null, null];
	let position = [0, 0];
	let angle = 0;

	const moveToPosition = (pos: number[]) => {

		if(!boundary[0] || pos[0] < boundary[0])
		{
			boundary[0] = pos[0];
		}
		
		if(!boundary[2] || pos[0] > boundary[2])
		{
			boundary[2] = pos[0];
		} 

		if(!boundary[1] || pos[1] < boundary[1])
		{
			boundary[1] = pos[1];
		}
		
		if(!boundary[3] || pos[1] > boundary[3])
		{
			boundary[3] = pos[1];
		} 

		position = pos;
	};
	moveToPosition([0, 0]);

	const addLine = (spec: SwimmingPoolSpecData) => {
		
		const elem : SwimmingPoolCoordData = {
			...spec,
			start: position,
			end: [position[0] + spec.length * Math.cos(angle), position[1] + spec.length * Math.sin(angle)],
			startAngle: angle,
			endAngle: angle
		};
		moveToPosition(elem.end);

		return elem;
	}

	const addRadius = (spec: SwimmingPoolSpecData) => {

		const a = spec.direction == 'l' ? (-Math.PI / 2) : (+Math.PI / 2);
		const middle = [position[0] + spec.length * Math.cos(angle), position[1] + spec.length * Math.sin(angle)];
		const end = [middle[0] + spec.length * Math.cos(angle + a), middle[1] + spec.length * Math.sin(angle + a)];	

		const elem : SwimmingPoolCoordData = {
			...spec,
			start: position,
			end: end,
			startAngle: angle,
			endAngle: angle + a
		}

		moveToPosition(elem.end);
		angle += a;

		return elem;
	};

	const addCorner = (spec: SwimmingPoolSpecData) => {

		const effectiveLength = spec.length;
		const a = spec.direction == 'l' ? (-Math.PI / 2) : (+Math.PI / 2);		
		const end = [position[0] + effectiveLength * Math.cos(angle) + effectiveLength * Math.cos(angle + a), position[1] + effectiveLength * Math.sin(angle) + effectiveLength * Math.sin(angle + a)];

		const elem : SwimmingPoolCoordData = {
			...spec,
			start: position,
			end: end,
			startAngle: angle,
			endAngle: angle + a
		}

		moveToPosition(elem.end);
		angle += a;

		return elem;
	};

	const add = (i: SwimmingPoolSpecData) : SwimmingPoolCoordData => 
	{
		let elem = null;
		switch(i.kind)
		{
			case SwimmingPoolConfiguratorShapeKind.middle:
			case SwimmingPoolConfiguratorShapeKind.middleSpacer:
			{
				elem = addLine(i);
				break;
			}
			case SwimmingPoolConfiguratorShapeKind.radius:
			{	
				elem = addRadius(i);
				break;
			}
			case SwimmingPoolConfiguratorShapeKind.corner:
			{
				elem = addCorner(i);
				break;
			}
		}

		return elem!;
	};

	const coordData = specs.map(spec => add(spec));
	const w = boundary[2]! - boundary[0]!;
	const h = boundary[3]! - boundary[1]!;
	
	const paddingX = (withDimensions === SwimmingPoolSchemaDimensionsType.none) ? 300 : (w > DIMENSION_LARGE_SIZE_BOUNDARY || h > DIMENSION_LARGE_SIZE_BOUNDARY) ? 4000 : 1500;
	const paddingY = (withDimensions === SwimmingPoolSchemaDimensionsType.none) ? 300 : (w > DIMENSION_LARGE_SIZE_BOUNDARY || h > DIMENSION_LARGE_SIZE_BOUNDARY) ? 4000 : 1500;

	const viewbox = [
		boundary[0]! - paddingX,
		boundary[1]! - paddingY,
		w + paddingX * 2,
		h + paddingY * 2
	].map(x => Math.floor(x))
	
	return [viewbox, coordData];
}


const makeElementsFromCoordData = (data: SwimmingPoolCoordData[]) : SwimmingPoolConfiguratorElementsFromCoordsOut =>
{
	let middleRemainder = 0;
	let index = 0;

	let joints = 0;
	const elems : ReactNode[] = [];
	for(let i = 0;i < data.length;i++)
	{
		const info = data[i];

		if(info.kind == SwimmingPoolConfiguratorShapeKind.middle)
		{
			let origin = info.start;
			const desiredLength = info.length;
			const angle = info.startAngle;
			const shortDiff : number = info.product!.thickness - info.product!.overlap;
			const overlapDiff : number = info.product!.overlap;

			for(let total = 0;total < desiredLength;)
			{
				const difference = Math.min(info.product!.length, desiredLength - total);

				let diff = difference;
				if(middleRemainder > 0)
				{
					joints++;
					diff = Math.min(middleRemainder, difference);
				}

				const end = [origin[0] + diff * Math.cos(angle), origin[1] + diff * Math.sin(angle)];

				const d = `
				M ${origin[0] - overlapDiff * Math.sin(angle)},${origin[1] + overlapDiff * Math.cos(angle)} 
				L ${origin[0] + shortDiff * Math.sin(angle)},${origin[1] - shortDiff * Math.cos(angle)}
				L ${end[0] + shortDiff * Math.sin(angle)},${end[1] - shortDiff * Math.cos(angle)}
				L ${end[0] - overlapDiff * Math.sin(angle)},${end[1] + overlapDiff * Math.cos(angle)}
				Z
				`;

				elems.push(<path xmlns="http://www.w3.org/2000/svg" d={d} className={middleRemainder > 0 ? (index % 2 ? 'odd' : 'even' ) : (++index % 2 ? 'odd' : 'even' )} stroke={'black'} />);
				middleRemainder = Math.max(0, info.product!.length - (desiredLength - total));
				total += diff;
				origin = end
			}
		}
		else if(info.kind == SwimmingPoolConfiguratorShapeKind.middleSpacer)
		{
			middleRemainder = 0;

			const d = `
				M ${info.start[0]},${info.start[1]}
				L ${info.end[0]},${info.end[1]}
				`;

			elems.push(<path xmlns="http://www.w3.org/2000/svg" d={d} className={'spacer'} stroke={'black'} />);
		}
		else if(info.kind == SwimmingPoolConfiguratorShapeKind.radius)
		{
			middleRemainder = 0;

			let origin = info.direction == 'l' ? info.end : info.start;
			const shortDiff : number = info.direction == 'l' ? (info.product!.overlap) : (info.product!.thickness - info.product!.overlap);
			const overlapDiff : number =  info.direction == 'l' ? (info.product!.thickness - info.product!.overlap) : info.product!.overlap;

			const sweepForward = info.direction == 'l' ? 0 : 1;
			const sweepBackward = info.direction == 'l' ? 1 : 0;
			const startAngle = info.startAngle > info.endAngle ? info.endAngle : info.startAngle;
			const endAngle = info.startAngle > info.endAngle ? info.startAngle : info.endAngle;
			const a = info.direction == 'l' ? (-Math.PI / 2) : (+Math.PI / 2);
			const middle = [info.start[0] + (info.product!.length /*- info.product!.overlap*/) * Math.cos(info.startAngle + a), info.start[1] + (info.product!.length /*- info.product!.overlap*/) * Math.sin(info.startAngle + a)]
			for(let total = startAngle;total < endAngle;)
			{
				const angularDiff : number = Math.min(info.product!.radius / 180 * Math.PI, endAngle - total);
				if(angularDiff < 0.01)
				{
					// Supresses rounding errors from radian-based calculations
					break;
				}

				const len = info.product!.length; 
				const end = [middle[0] + len * Math.cos(total - a + angularDiff), middle[1] + len * Math.sin(total - a + angularDiff)]

				//elems.push(<circle xmlns="http://www.w3.org/2000/svg" cx={end[0]} cy={end[1]} r="30" className={elems.length % 2 ? 'odd' : 'even'} stroke={'yellow'} fill="red" />);
				//elems.push(<circle xmlns="http://www.w3.org/2000/svg" cx={origin[0] + overlapDiff * Math.cos(total + a)} cy={origin[1] + overlapDiff * Math.sin(total + a)} r="30" className={elems.length % 2 ? 'odd' : 'even'} stroke={'yellow'} fill="red" />);
				//elems.push(<circle xmlns="http://www.w3.org/2000/svg" cx={end[0] - shortDiff * Math.cos(total + angularDiff + a)} cy={end[1] - shortDiff  * Math.sin(total + angularDiff + a)} r="30" className={elems.length % 2 ? 'odd' : 'even'} stroke={'yellow'} fill="red" />);

				const d = `
				M ${origin[0] + overlapDiff * Math.cos(total + a)},${origin[1] + overlapDiff * Math.sin(total + a)} 
				L ${origin[0] - shortDiff * Math.cos(total + a)},${origin[1] - shortDiff * Math.sin(total + a)}
				A -${len-info.product!.overlap+info.product!.thickness} -${len-info.product!.overlap+info.product!.thickness} 0 0 ${info.direction == 'r' ? sweepForward : sweepBackward}  ${end[0] - shortDiff * Math.cos(total + angularDiff + a)} ${end[1] - shortDiff  * Math.sin(total + angularDiff + a)}
				L ${end[0] + overlapDiff * Math.cos(total + angularDiff + a)},${end[1] + overlapDiff * Math.sin(total + angularDiff + a)}
				A -${len} -${len} 0 0 ${info.direction == 'l' ? sweepForward : sweepBackward}  ${origin[0] + overlapDiff * Math.cos(total + a)} ${origin[1] + overlapDiff * Math.sin(total + a)} 
				`;


				elems.push(<path xmlns="http://www.w3.org/2000/svg" d={d} className={++index % 2 ? 'odd' : 'even'} stroke={'black'} />);
				total = (total + angularDiff);
				origin = end
			}
		}
		else if(info.kind == SwimmingPoolConfiguratorShapeKind.corner && info.direction == 'r')
		{
			middleRemainder = 0;

			const effectiveLength = info.length;
			const origin = info.start;
			const middle = [info.start[0] + effectiveLength * Math.cos(info.startAngle), info.start[1] + effectiveLength * Math.sin(info.startAngle)];
			const end = info.end;

			const a = (+Math.PI / 2);
			const shortDiff : number = info.product!.thickness - info.product!.overlap;
			const overlapDiff : number = info.product!.overlap;

			const sign = (Math.floor(info.startAngle / Math.PI) % 2 != Math.floor(info.endAngle / Math.PI) % 2) ? -1 : 1;
			const d = `
			M ${origin[0] - shortDiff * Math.cos(info.startAngle + a)},${origin[1] - shortDiff * Math.sin(info.startAngle + a)} 
			L ${origin[0] + overlapDiff * Math.cos(info.startAngle + a)},${origin[1] + overlapDiff * Math.sin(info.startAngle + a)} 
			L ${middle[0] - 1.41 * sign * overlapDiff * Math.cos(info.endAngle - Math.PI / 4.0)},${middle[1] + 1.41 * sign * overlapDiff * Math.sin(info.endAngle - Math.PI / 4.0)} 
			L ${end[0] + overlapDiff * Math.cos(info.endAngle + a)},${end[1] + overlapDiff * Math.sin(info.endAngle + a)} 
			L ${end[0] - shortDiff * Math.cos(info.endAngle + a)},${end[1] - shortDiff * Math.sin(info.endAngle + a)} 
			L ${middle[0] + 1.41 * sign * shortDiff * Math.cos(info.endAngle - Math.PI / 4.0)},${middle[1] - 1.41 * sign * shortDiff * Math.sin(info.endAngle - Math.PI / 4.0)} 
			Z
			`;

			elems.push(<path xmlns="http://www.w3.org/2000/svg" d={d} className={++index % 2 ? 'odd' : 'even'} stroke={'black'} />);			
		}
		else if(info.kind == SwimmingPoolConfiguratorShapeKind.corner && info.direction == 'l')
		{
			middleRemainder = 0.

			const effectiveLength = info.length;
			const origin = info.start;
			const middle = [info.start[0] + effectiveLength * Math.cos(info.startAngle), info.start[1] + effectiveLength * Math.sin(info.startAngle)];
			const end = info.end;

			const a = (-Math.PI / 2);
			const shortDiff : number = info.product!.thickness - info.product!.overlap;
			const overlapDiff : number = info.product!.overlap;

			const sign = (Math.floor(info.startAngle / Math.PI) % 2 != Math.floor(info.endAngle / Math.PI) % 2) ? -1 : 1;
			const d = `
			M ${origin[0] + shortDiff * Math.cos(info.startAngle + a)},${origin[1] + shortDiff * Math.sin(info.startAngle + a)} 
			L ${origin[0] - overlapDiff * Math.cos(info.startAngle + a)},${origin[1] - overlapDiff * Math.sin(info.startAngle + a)} 
			L ${middle[0] + 1.41 * sign * overlapDiff * Math.cos(info.endAngle - Math.PI / 4.0)},${middle[1] + 1.41 * sign * overlapDiff * Math.sin(info.endAngle - Math.PI / 4.0)} 
			L ${end[0] - overlapDiff * Math.cos(info.endAngle + a)},${end[1] + overlapDiff * Math.sin(info.endAngle + a)} 
			L ${end[0] + shortDiff * Math.cos(info.endAngle + a)},${end[1] - shortDiff * Math.sin(info.endAngle + a)} 
			L ${middle[0] - 1.41 * sign * shortDiff * Math.cos(info.endAngle - Math.PI / 4.0)},${middle[1] - 1.41 * sign * shortDiff * Math.sin(info.endAngle - Math.PI / 4.0)} 
			Z
			`;

			// elems.push(<circle xmlns="http://www.w3.org/2000/svg" cx={origin[0]} cy={origin[1]} r="30" className={elems.length % 2 ? 'odd' : 'even'} stroke={'yellow'} fill="red" />);
			// elems.push(<circle xmlns="http://www.w3.org/2000/svg" cx={end[0]} cy={end[1]} r="30" className={elems.length % 2 ? 'odd' : 'even'} stroke={'yellow'} fill="red" />);
			// elems.push(<circle xmlns="http://www.w3.org/2000/svg" cx={middle[0]} cy={middle[1]} r="30" className={elems.length % 2 ? 'odd' : 'even'} stroke={'yellow'} fill="yellow" />);

			elems.push(<path xmlns="http://www.w3.org/2000/svg" d={d} className={++index % 2 ? 'odd' : 'even'} stroke={'black'} />);			
		}
	}

	return {
		middleJoints: joints,
		elems: elems
	};

}

const makeDimensionsElems = (type: SwimmingPoolSchemaDimensionsType, coordData: SwimmingPoolCoordData[], sizeX: number, sizeY: number, large: boolean) : ReactElement[] =>
{
	const size = Math.min(sizeX, sizeY)
	const elems = [];
	let i = 0
	for(const info of coordData)
	{
		if(info.editable)
		{
			const a = (info.startAngle - Math.PI / 2) % Math.PI;
			const s = [info.start[0] - info.subtractStart * Math.cos(info.startAngle), info.start[1] - info.subtractStart * Math.sin(info.startAngle)];
			const e = [info.end[0] + info.subtractEnd * Math.cos(info.endAngle), info.end[1] + info.subtractEnd * Math.sin(info.endAngle)];
			
			let usedOp = false;
			const op = () => {
				if(!usedOp)
				{
					usedOp = true;
					return 'M';
				}

				return 'L';
			}

			let d = '';
			// nedavam pocatecni kdyz predchozi uhel je roven a - PI  
			const previousDifference = Math.abs(a - info.previousStartAngle!);
			if(previousDifference != Math.PI)
			{
				d += `
				${op()} ${s[0]} ${s[1]}
				${op()} ${s[0] + 1.05 * size * Math.cos(a)} ${s[1] + 1.05 * size * Math.sin(a)}
				`;
			}
			
			d += `
			${op()} ${s[0] + 1 * size * Math.cos(a)} ${s[1] + 1 * size * Math.sin(a)}
			${op()} ${e[0] + 1 * size * Math.cos(a)} ${e[1] + 1 * size * Math.sin(a)}
			`;
			
			// nedavam koncovou kdyz nasledujici uhel je roven a 
			const nextDifference = Math.abs(info.nextEndAngle! - a);
			if(nextDifference != 0)
			{
				d += `
				${op()} ${e[0] + 1.05 * size * Math.cos(a)} ${e[1] + 1.05 * size * Math.sin(a)}
				${op()} ${e[0]} ${e[1]}
				`;
			}

			elems.push(<path xmlns="http://www.w3.org/2000/svg" d={d} className={'dimension'} stroke={'black'} />);	

			const text = type == SwimmingPoolSchemaDimensionsType.letter ? 
				String.fromCharCode('A'.charCodeAt(0) + (i++)) : 
				Math.round((info.length + info.subtractStart + info.subtractEnd));
			const anchor = a != 0 ? 'middle' : 'left';
			const baseline = a == 0 ? 'middle' : 'baseline';
			const textbottom = [(s[0] + e[0]) / 2.0 + 1.15 * size * Math.cos(a), (s[1] + e[1]) / 2.0 + 1.15 * size * Math.sin(a)];
			elems.push(<text xmlns="http://www.w3.org/2000/svg" x={textbottom[0]} alignmentBaseline={baseline} text-anchor={anchor} y={textbottom[1]} className={classNames('dimension', large ? 'dimension-large' : '')} stroke={'black'}>{text}</text>);	

		}
	}

	return elems;
}

export const renderSwimmingPoolSvg = (specData : SwimmingPoolSpecData[], variant: SwimmingPoolConfiguratorVariantFragment, withDimensions: SwimmingPoolSchemaDimensionsType) => {

	const cloned = JSON.parse(JSON.stringify(specData));
	const withProducts = determineProductsAndLengthsForSpecData(cloned, variant);
	const subtracted = subtractAnglesFromMiddleSpecPoints(withProducts);
	const [viewbox, data] = makeCoordDataFromSpecData(subtracted, withDimensions);
	const withNeighourAngles = determineNeighbourAngles(data);

	let {elems} = makeElementsFromCoordData(withNeighourAngles);
	if(withDimensions !== SwimmingPoolSchemaDimensionsType.none)
	{
		elems = [...elems, makeDimensionsElems(
			withDimensions,
			withNeighourAngles, 
			viewbox[2] / 12.0,
			viewbox[3] / 12.0,
			viewbox[2] > DIMENSION_LARGE_SIZE_BOUNDARY || viewbox[3] > DIMENSION_LARGE_SIZE_BOUNDARY
		)]
	}

	return (
		<svg xmlns="http://www.w3.org/2000/svg" viewBox={ viewbox.join(',') } width="100%" preserveAspectRatio="xMidYMid meet">
			{ elems }
		</svg>
	);
};

export const calculateSwimmingPoolDetails = (specData : SwimmingPoolSpecData[], variant: SwimmingPoolConfiguratorVariantFragment) : SwimmingPoolConstructionDetails =>
{
	const cloned = JSON.parse(JSON.stringify(specData));
	const withProducts = determineProductsAndLengthsForSpecData(cloned, variant);
	const subtracted = subtractAnglesFromMiddleSpecPoints(withProducts);
	const [_, data] = makeCoordDataFromSpecData(subtracted, SwimmingPoolSchemaDimensionsType.none);
	const withNeighourAngles = determineNeighbourAngles(data);
	let {middleJoints} = makeElementsFromCoordData(withNeighourAngles);

	let w = 0;
	let l = 0;
	let c = 0;
	const reserve : {[prop: string] : number} = {};
	const counts : {[prop: string] : number} = {};
	const exactCounts : {[prop: string] : number} = {};
	for(const it of data)
	{
		if(it.kind == SwimmingPoolConfiguratorShapeKind.middle || it.kind == SwimmingPoolConfiguratorShapeKind.middleSpacer)
		{
			if(it.startAngle == 0 || it.endAngle == 0)
			{
				l += it.length + it.subtractStart + it.subtractEnd;
			}
			else if(it.startAngle == Math.PI / 2 || it.endAngle == Math.PI / 2)
			{
				w += it.length + it.subtractStart + it.subtractEnd;
			}
		}

		if(it.kind == SwimmingPoolConfiguratorShapeKind.radius)
		{
			c += 2 * Math.PI * it.length * 0.25;
		}
		else if(it.kind != SwimmingPoolConfiguratorShapeKind.corner)
		{
			c += it.length;
		}
		else if(it.kind == SwimmingPoolConfiguratorShapeKind.corner)
		{
			c += 2 * it.length;
		}

		if(it.product)
		{
			if(counts[it.product.product.id] === undefined)
			{
				counts[it.product.product.id] = 0;	
				exactCounts[it.product.product.id] = 0;	
				reserve[it.product.product.id] = 0;	
			}

			if(it.product.kind == SwimmingPoolConfiguratorShapeKind.radius)
			{
				reserve[it.product.product.id] += 0.5;
				counts[it.product.product.id] += Math.ceil(90.0 / it.product.radius);
				exactCounts[it.product.product.id] += (90.0 / it.product.radius);
			}
			else if(it.product.kind == SwimmingPoolConfiguratorShapeKind.corner && it.direction == 'r')
			{
				reserve[it.product.product.id] += 0.1;
				counts[it.product.product.id] += Math.ceil((it.length + it.product.thickness - it.product.overlap) / it.product.length) * (it.product.count ?? 1);
				exactCounts[it.product.product.id] += ((it.length + it.product.thickness - it.product.overlap) / it.product.length) * (it.product.count ?? 1);
			}
			else if(it.product.kind == SwimmingPoolConfiguratorShapeKind.corner && it.direction == 'l')
			{
				reserve[it.product.product.id] += 0.1;
				counts[it.product.product.id] += Math.ceil((it.length) / it.product.length) * (it.product.count ?? 1);
				exactCounts[it.product.product.id] += ((it.length) / it.product.length) * (it.product.count ?? 1);
			}
			else 
			{
				reserve[it.product.product.id] += 1;
				counts[it.product.product.id] += Math.ceil((it.length) / it.product.length);
				exactCounts[it.product.product.id] += ((it.length) / it.product.length);

				if(middleJoints-- > 0)
				{
					reserve[it.product.product.id] -= 1;
					counts[it.product.product.id] -= 1;
				}
			}			
		}	
	}

	for(const key in reserve)
	{
		reserve[key] = Math.ceil(reserve[key]);
	}

	const product = variant.products.find(p => p.kind == SwimmingPoolConfiguratorShapeKind.middle);
	return {
		innerWidth: w,
		innerLength: l,
		outerWidth: w + 2 * product!.thickness - 2 * product!.overlap,
		outerLength: l + 2 * product!.thickness - 2 * product!.overlap,
		circumference: c,	
		reserveCounts: reserve,
		productCounts: counts,
		productExactCounts: exactCounts
	}
}